// ─── 型定義 ─────────────────────────────────────────

export type AxisScale = {
  min: number;
  max: number;
  ticks: number[];
  tickFormat: (n: number) => string;
};

// ─── 描画レイアウト定数 ─────────────────────────────

export const PLOT_LAYOUT = {
  top: 80,
  right: 80,
  bottom: 100,
  left: 120,
  width: 1920,
  height: 1080,
  /** プロット領域の実幅 */
  get plotWidth() {
    return this.width - this.left - this.right;
  },
  /** プロット領域の実高 */
  get plotHeight() {
    return this.height - this.top - this.bottom;
  },
} as const;

export type PlotLayout = typeof PLOT_LAYOUT;

// ─── Nice Ticks 計算 ────────────────────────────────

/** d3 風の nice step: 1, 2, 5, 10, 20, 50, ... */
function niceStep(rawStep: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;
  let nice: number;
  if (residual <= 1.5) nice = 1;
  else if (residual <= 3) nice = 2;
  else if (residual <= 7) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

/**
 * データ値の配列からスケール (min/max/ticks) を算出する。
 * @param values データ値 (null は除外済みであること)
 * @param paddingRatio 範囲の上下に加える余白比率 (デフォルト 0.1)
 * @param targetTickCount 目標目盛数 (デフォルト 6)
 */
export function computeScale(
  values: number[],
  paddingRatio = 0.1,
  targetTickCount = 6,
): AxisScale {
  if (values.length === 0) {
    return { min: 0, max: 100, ticks: [0, 20, 40, 60, 80, 100], tickFormat: String };
  }

  let dataMin = Math.min(...values);
  let dataMax = Math.max(...values);

  // 同値の場合のガード
  if (dataMin === dataMax) {
    dataMin = dataMin > 0 ? 0 : dataMin - 1;
    dataMax = dataMax > 0 ? dataMax * 2 || 1 : 0;
  }

  const range = dataMax - dataMin;
  const padding = range * paddingRatio;
  const rawMin = Math.max(0, dataMin - padding); // 支出金額は 0 以上
  const rawMax = dataMax + padding;

  const rawStep = (rawMax - rawMin) / targetTickCount;
  const step = niceStep(rawStep);

  const niceMin = Math.floor(rawMin / step) * step;
  const niceMax = Math.ceil(rawMax / step) * step;

  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step * 0.01; v += step) {
    ticks.push(Math.round(v * 1e6) / 1e6); // 浮動小数点誤差対策
  }

  const tickFormat = (n: number): string => {
    if (n >= 10000) return `${(n / 10000).toFixed(n % 10000 === 0 ? 0 : 1)}万`;
    return n.toLocaleString("ja-JP");
  };

  return { min: niceMin, max: niceMax, ticks, tickFormat };
}

// ─── 座標変換 ───────────────────────────────────────

/**
 * データ値を SVG ピクセル座標に変換する。
 * Y 軸は反転 (上が大きい値) するため pixelMin > pixelMax で渡す。
 */
export function dataToSvg(
  value: number,
  scale: AxisScale,
  pixelMin: number,
  pixelMax: number,
): number {
  const t = (value - scale.min) / (scale.max - scale.min);
  return pixelMin + t * (pixelMax - pixelMin);
}
